import { Button, Dropdown } from '@120wateraudit/envirio-components';
import gql from 'graphql-tag';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { DropdownProps } from 'semantic-ui-react';
import { Props } from 'src/containers/AccountSoftwarePackages';
import {
  useAvailableFeatures,
  useCreateAccountFeatureMutation
} from 'src/hooks/useFeatures';
import styled from 'src/theme';
import { Feature } from 'src/types/Feature';
import { toast } from 'react-toastify';
import { apolloClient } from 'src/apolloClient';
import { Account } from 'src/types/AccountManagementTypes';

const GET_ACCOUNT_QUERY = gql`
  query getAccount($id: Int!) {
    account(id: $id) {
      id
      isAccountDisabled
      isDemo
    }
  }
`;

const SYNC_ACCOUNT_MUTATION = gql`
  mutation syncAccount($account: UpdateAccountInputType!) {
    updateAccount(account: $account) {
      id
    }
  }
`;

export const AddFeature = ({ accountId }: Props) => {
  const [selectedFeature, setSelectedFeature] = useState<number | undefined>(
    undefined
  );
  const [accountDetails, setAccountDetails] = useState<Account | null>(null);
  const { availableFeatures } = useAvailableFeatures(accountId);

  const [createAccountFeature] = useCreateAccountFeatureMutation();

  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const { data } = await apolloClient.query({
          query: GET_ACCOUNT_QUERY,
          variables: { id: accountId }
        });

        if (data && data.account) {
          setAccountDetails(data.account);
        }
      } catch (error) {
        toast.error('Error fetching account details. Please try again.');
      }
    };

    fetchAccountDetails();
  }, [accountId]);

  const handleSyncAccount = async (variables: any) => {
    try {
      return await apolloClient.mutate({
        mutation: SYNC_ACCOUNT_MUTATION,
        variables
      });
    } catch (error) {
      toast.error('Error syncing account. Please try again.');
      throw error;
    }
  };

  const onSelectedFeatureChanged = (
    event: React.SyntheticEvent<HTMLElement>,
    { value }: DropdownProps
  ) => {
    if (typeof value === 'number') {
      setSelectedFeature(value);
    }
  };

  const onAddFeatureClicked = async () => {
    if (selectedFeature) {
      try {
        await createAccountFeature({
          variables: {
            accountId,
            featureId: selectedFeature
          },
          refetchQueries: ['features', 'availableFeatures']
        });

        if (accountDetails) {
          await handleSyncAccount({
            account: {
              id: accountId,
              isAccountDisabled: accountDetails.isAccountDisabled,
              isDemo: accountDetails.isDemo
            }
          });
        } else {
          await handleSyncAccount({
            account: {
              id: accountId,
              isAccountDisabled: false,
              isDemo: false
            }
          });
        }

        setSelectedFeature(undefined);
      } catch (error) {
        toast.error('Error adding feature. Please try again.');
      }
    }
  };

  return (
    <UpdateFeaturesWrapper>
      <ModalDropdown
        label="Add Feature"
        options={getFeaturesDropdown(availableFeatures)}
        placeholder={'Select Individual Feature'}
        onChange={onSelectedFeatureChanged}
        fluid
        optionTextMaxLength={45}
        search
      />
      <Button variant="primary" onClick={onAddFeatureClicked}>
        ADD FEATURE
      </Button>
    </UpdateFeaturesWrapper>
  );
};

export const getFeaturesDropdown = (
  features: Feature[] | undefined
): DropdownProps['options'] => {
  return features?.map(f => ({
    text: f.name,
    value: f.id
  }));
};

const ModalDropdown = styled(Dropdown)`
  margin-bottom: 1rem;
`;

const UpdateFeaturesWrapper = styled.div`
   {
    width: 50%;
    padding: 0 15px;
  }
`;
